import { AuthMeResult, SessionInfo } from '@apm/contracts';
import { useSyncExternalStore } from 'react';
import { closeNotifications } from '../alerts/notify';
import { failureMessage, readJson, request, setUnauthorizedHandler } from '../api';
import { streamStore } from '../store/stream';
import { AUTH_TEXT } from '../terms';

/**
 * 관제 화면 로그인 상태 (51회차).
 *
 * 세션 쿠키는 `HttpOnly`라 화면이 읽을 수 없다. 그래서 `GET /api/auth/me`로 묻고, 답을 스토어 하나에
 * 들고 있는다. 실시간 스토어(`store/stream.ts`)와 같은 `useSyncExternalStore` 모양이다.
 *
 * 상태는 다섯이다.
 *
 * | 상태 | 화면 |
 * | --- | --- |
 * | `checking` | 아직 모른다. 로그인 화면도 본 화면도 그리지 않는다(깜빡임을 막는다) |
 * | `signedOut` | 로그인 화면 |
 * | `signedIn` | 본 화면. 머리에 계정 이름과 로그아웃 |
 * | `disabled` | 서버가 로그인을 껐다(`APM_AUTH_ENABLED=false`). 본 화면, 로그아웃 없음 |
 * | `unreachable` | 서버에 못 닿았다. 로그인 화면에 까닭을 적는다 |
 *
 * 어느 조회든 세션 401을 받으면 `api.ts`가 `signedOut()`을 부른다. 그때와 로그아웃 때 실시간 스토어를
 * 비운다 — 로그인 화면 뒤에 앞사람이 본 요청이 남지 않게.
 */

export type SessionStatus = 'checking' | 'signedOut' | 'signedIn' | 'disabled' | 'unreachable';

export interface SessionState {
  status: SessionStatus;
  /** `signedIn`일 때만 */
  session?: SessionInfo;
  /** `unreachable`일 때, 또는 로그아웃을 서버에 못 알렸을 때의 한글 문구 */
  error?: string;
  /** `signedOut`인데 서버에 로그아웃을 못 알렸다. 쿠키가 남아 있을 수 있다 (리뷰 R51-C-1) */
  logoutFailed?: boolean;
}

const ME_URL = '/api/auth/me';
const LOGIN_URL = '/api/auth/login';
const LOGOUT_URL = '/api/auth/logout';

class SessionStateStore {
  private state: SessionState = { status: 'checking' };
  private readonly listeners = new Set<() => void>();

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getState = (): SessionState => this.state;

  /** 상태를 통째로 바꾼다. 시험이 로그인한 화면을 바로 그릴 때도 쓴다 */
  replace(next: SessionState): void {
    this.state = next;
    for (const listener of this.listeners) listener();
  }
}

/** 화면 전체가 공유하는 로그인 상태 하나 */
export const sessionStore = new SessionStateStore();

/** 세션이 풀렸다. 실시간 상태를 비우고 로그인 화면으로 */
export function signedOut(): void {
  clearScreen();
  if (sessionStore.getState().status === 'signedOut') return;
  sessionStore.replace({ status: 'signedOut' });
}

setUnauthorizedHandler(signedOut);

/** 겹쳐 부르면(개발 모드의 StrictMode 이중 실행) 한 번만 묻는다 */
let pendingCheck: Promise<void> | undefined;

/** `GET /api/auth/me`로 지금 세션을 묻는다 */
export function checkSession(): Promise<void> {
  pendingCheck ??= runCheck().finally(() => {
    pendingCheck = undefined;
  });
  return pendingCheck;
}

async function runCheck(): Promise<void> {
  try {
    const response = await request(ME_URL);
    if (response.status === 401) {
      sessionStore.replace({ status: 'signedOut' });
      return;
    }
    if (!response.ok) {
      sessionStore.replace({
        status: 'unreachable',
        error: await failureMessage(response, AUTH_TEXT.checkFailed),
      });
      return;
    }
    const me = await readJson(response, AuthMeResult);
    if (!me.authEnabled) sessionStore.replace({ status: 'disabled' });
    else if (me.session === undefined) sessionStore.replace({ status: 'signedOut' });
    else sessionStore.replace({ status: 'signedIn', session: me.session });
  } catch (err) {
    sessionStore.replace({
      status: 'unreachable',
      error: err instanceof Error ? err.message : String(err),
    });
  }
}

/** 로그인. 실패하면 화면에 그대로 띄울 한글 문구를, 성공하면 undefined를 준다 */
export async function login(username: string, password: string): Promise<string | undefined> {
  // 늦게 온 로그아웃 응답이 새 로그인 쿠키를 지우지 않게 로그아웃이 끝나기를 기다린다 (리뷰 R51-C-2)
  if (pendingLogout !== undefined) await pendingLogout;
  try {
    const response = await request(LOGIN_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    // 서버가 준 한글 문구를 그대로 쓴다. 틀린 쪽이 아이디인지 비밀번호인지는 서버도 가르지 않는다
    if (!response.ok) return await failureMessage(response, AUTH_TEXT.failed);
    const session = await readJson(response, SessionInfo);
    sessionStore.replace({ status: 'signedIn', session });
    return undefined;
  } catch (err) {
    return err instanceof Error ? err.message : String(err);
  }
}

/** 로그아웃 요청을 기다리는 상한. 넘기면 요청을 끊고 실패로 본다 (리뷰 R51-C-1) */
const LOGOUT_TIMEOUT_MS = 10_000;

/** 진행 중인 로그아웃. 끝나기 전에는 로그인 요청을 보내지 않는다 (리뷰 R51-C-2) */
let pendingLogout: Promise<void> | undefined;

/**
 * 로그아웃. **화면을 먼저 비우고** 서버에는 그 뒤에 알린다 (리뷰 R51-B-1). 서버 답을 기다리면
 * 프록시가 요청을 붙들고 있는 동안 계정 이름·조회 결과·실시간 연결이 그대로 남는다.
 *
 * 서버에 못 알렸으면(실패·상한 초과) 쿠키와 서버 세션이 남아 새로고침하면 다시 들어가진다.
 * 그래서 로그인 화면에 그 까닭과 `다시 로그아웃`을 띄운다(`logoutFailed`, 리뷰 R51-C-1).
 */
export function logout(): Promise<void> {
  clearScreen();
  const mine: SessionState = { status: 'signedOut' };
  sessionStore.replace(mine);
  const run = tellServer(mine).finally(() => {
    if (pendingLogout === run) pendingLogout = undefined;
  });
  pendingLogout = run;
  return run;
}

async function tellServer(mine: SessionState): Promise<void> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let ok = false;
  try {
    const timeout = new Promise<'timeout'>((resolve) => {
      timer = setTimeout(() => {
        // 끊어 두어야 뒤늦게 온 응답의 `Max-Age=0`이 다음 로그인 쿠키를 지우지 않는다
        controller.abort();
        resolve('timeout');
      }, LOGOUT_TIMEOUT_MS);
    });
    const result = await Promise.race([
      request(LOGOUT_URL, { method: 'POST', signal: controller.signal }),
      timeout,
    ]);
    ok = result !== 'timeout' && result.ok;
  } catch {
    ok = false;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
  // 그사이 다른 상태(다시 로그인·다시 확인)로 넘어갔으면 건드리지 않는다
  if (!ok && sessionStore.getState() === mine) {
    sessionStore.replace({
      status: 'signedOut',
      logoutFailed: true,
      error: AUTH_TEXT.logoutFailed,
    });
  }
}

/** 앞사람이 본 것을 지운다. 실시간 상태와 띄운 브라우저 알림 (리뷰 R51-B-3) */
function clearScreen(): void {
  streamStore.reset();
  closeNotifications();
}

export function useSession(): SessionState {
  return useSyncExternalStore(sessionStore.subscribe, sessionStore.getState, sessionStore.getState);
}
